/**
 * LEONARIS Recovery Hub — Minimal JS
 * Handles: smooth scroll, card hover focus, scroll-triggered fade-in
 */

(function () {
  'use strict';

  // ── Smooth scroll for in-page CTA links ──────────────────────────────────
  document.addEventListener('click', function (e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    var id = anchor.getAttribute('href');
    if (id === '#') return;
    var target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ── Intersection Observer: fade-in on scroll ─────────────────────────────
  if ('IntersectionObserver' in window) {
    var style = document.createElement('style');
    style.textContent = [
      '.lhub-fade { opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }',
      '.lhub-fade.lhub-fade--visible { opacity: 1; transform: translateY(0); }',
    ].join('');
    document.head.appendChild(style);

    var applyFade = function (selector, delay) {
      document.querySelectorAll(selector).forEach(function (el, i) {
        el.classList.add('lhub-fade');
        el.style.transitionDelay = (i * (delay || 80)) + 'ms';
      });
    };

    applyFade('.lhub-card', 60);
    applyFade('.lhub-journey__step', 100);

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('lhub-fade--visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    document.querySelectorAll('.lhub-fade').forEach(function (el) {
      observer.observe(el);
    });

    // Respect prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.lhub-fade').forEach(function (el) {
        el.classList.add('lhub-fade--visible');
        el.style.transitionDelay = '0ms';
      });
    }
  }

  // ── Recovery-Fortschritt (nur localStorage, keine Serveranfragen, keine Kundendaten) ──

  var LHUB_KEYS = {
    completed: 'leonaris_evening_completed',
    lastDate: 'leonaris_last_ritual_date',
    total: 'leonaris_total_rituals',
    streak: 'leonaris_current_streak',
    history: 'leonaris_ritual_history'
  };

  function lhubStorageAvailable() {
    try {
      var testKey = '__lhub_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (err) {
      return false;
    }
  }

  var LHUB_STORAGE_OK = lhubStorageAvailable();

  function safeGetItem(key) {
    if (!LHUB_STORAGE_OK) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function safeSetItem(key, value) {
    if (!LHUB_STORAGE_OK) return false;
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (err) {
      return false;
    }
  }

  function getLocalDateString(date) {
    var d = date || new Date();
    var year = d.getFullYear();
    var month = String(d.getMonth() + 1);
    var day = String(d.getDate());
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return year + '-' + month + '-' + day;
  }

  function parseLocalDateString(str) {
    if (typeof str !== 'string') return null;
    var parts = str.split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;
    var d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return d;
  }

  function getSafeInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }

  function getRitualHistory() {
    var raw = safeGetItem(LHUB_KEYS.history);
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (entry) {
        return typeof entry === 'string' && parseLocalDateString(entry) !== null;
      });
    } catch (err) {
      return [];
    }
  }

  function saveRitualHistory(history) {
    try {
      safeSetItem(LHUB_KEYS.history, JSON.stringify(history));
    } catch (err) {
      /* still gracefully ignored */
    }
  }

  function calculateCurrentStreak(history) {
    if (!history || history.length === 0) return 0;

    var dates = history
      .map(parseLocalDateString)
      .filter(function (d) { return d !== null; })
      .sort(function (a, b) { return b - a; });

    if (dates.length === 0) return 0;

    var today = parseLocalDateString(getLocalDateString());
    var oneDayMs = 24 * 60 * 60 * 1000;

    var mostRecent = dates[0];
    var diffFromToday = Math.round((today - mostRecent) / oneDayMs);

    // Letzter Abschluss liegt mehr als 1 Tag zurück → aktuelle Serie beendet
    if (diffFromToday > 1) return 0;

    var streak = 1;
    var cursor = mostRecent;

    for (var i = 1; i < dates.length; i++) {
      var expectedPrev = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
      if (dates[i].getTime() === expectedPrev.getTime()) {
        streak++;
        cursor = dates[i];
      } else if (dates[i].getTime() === cursor.getTime()) {
        continue; // Duplikat am selben Kalendertag — überspringen
      } else {
        break;
      }
    }

    return streak;
  }

  function recordEveningRitualCompletion() {
    var todayStr = getLocalDateString();
    var history = getRitualHistory();

    if (history.indexOf(todayStr) !== -1) {
      // Heute bereits abgeschlossen — kein doppelter Eintrag, kein erneutes Event
      return { alreadyCompleted: true, date: todayStr };
    }

    history.push(todayStr);
    history.sort();
    saveRitualHistory(history);

    safeSetItem(LHUB_KEYS.completed, 'true');
    safeSetItem(LHUB_KEYS.lastDate, todayStr);
    safeSetItem(LHUB_KEYS.total, String(getSafeInt(safeGetItem(LHUB_KEYS.total)) + 1));
    safeSetItem(LHUB_KEYS.streak, String(calculateCurrentStreak(history)));

    try {
      document.dispatchEvent(new CustomEvent('leonaris:ritual-completed', {
        detail: { ritual: 'evening', date: todayStr }
      }));
    } catch (err) {
      /* CustomEvent nicht verfügbar — kein Abbruch der übrigen Logik */
    }

    return { alreadyCompleted: false, date: todayStr };
  }

  function lhubFormatDisplayDate(dateStr) {
    var date = parseLocalDateString(dateStr);
    if (!date) return null;

    var todayStr = getLocalDateString();
    var yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    var yesterdayStr = getLocalDateString(yesterdayDate);

    if (dateStr === todayStr) return 'Heute';
    if (dateStr === yesterdayStr) return 'Gestern';

    try {
      return date.toLocaleDateString('de-DE');
    } catch (err) {
      return dateStr;
    }
  }

  function renderRecoveryProgress() {
    var section = document.querySelector('[data-lhub-progress]');
    if (!section) return;

    var history = getRitualHistory();
    var total = getSafeInt(safeGetItem(LHUB_KEYS.total));
    var lastDate = safeGetItem(LHUB_KEYS.lastDate);
    var streak = calculateCurrentStreak(history);

    var totalEl = section.querySelector('[data-lhub-total]');
    if (totalEl) {
      totalEl.textContent = total + ' abgeschlossen';
    }

    var routineEl = section.querySelector('[data-lhub-routine]');
    if (routineEl) {
      if (streak >= 2) {
        routineEl.textContent = streak + ' Tage in Folge';
      } else if (total > 0) {
        routineEl.textContent = 'Du bist gestartet';
      } else {
        routineEl.textContent = 'Starte heute';
      }
    }

    var lastDateEl = section.querySelector('[data-lhub-last-date]');
    if (lastDateEl) {
      var formatted = lastDate ? lhubFormatDisplayDate(lastDate) : null;
      lastDateEl.textContent = formatted || 'Noch nicht gestartet';
    }

    var ctaEl = section.querySelector('[data-lhub-progress-cta]');
    if (ctaEl) {
      ctaEl.textContent = total > 0 ? 'Routine fortsetzen' : 'Erstes Ritual beginnen';
    }
  }

  function initEveningCompletionButton() {
    var btn = document.querySelector('[data-lhub-complete-evening]');
    if (!btn) return;

    var defaultLabel = btn.getAttribute('data-label-default') || btn.textContent.trim();
    var doneLabel = btn.getAttribute('data-label-done') || defaultLabel;
    var noteEl = document.querySelector('[data-lhub-storage-note]');
    var noteDefaultText = noteEl ? noteEl.textContent : '';

    var setDoneState = function () {
      btn.textContent = doneLabel;
      btn.setAttribute('data-completed', 'true');
      btn.setAttribute('aria-disabled', 'true');
      btn.disabled = true;
    };

    var showSavedNote = function () {
      if (!noteEl) return;
      noteEl.textContent = noteDefaultText;
      noteEl.hidden = false;
    };

    var showStorageWarning = function () {
      if (!noteEl) return;
      noteEl.textContent = 'Der Fortschritt konnte auf diesem Gerät nicht gespeichert werden.';
      noteEl.hidden = false;
    };

    var history = getRitualHistory();
    if (history.indexOf(getLocalDateString()) !== -1) {
      setDoneState();
      showSavedNote();
    }

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      recordEveningRitualCompletion();
      setDoneState();
      if (LHUB_STORAGE_OK) {
        showSavedNote();
      } else {
        showStorageWarning();
      }
      renderRecoveryProgress();
    });
  }

  function initLeonarisHub() {
    try {
      initEveningCompletionButton();
      renderRecoveryProgress();
    } catch (err) {
      /* defensiv: Fortschrittslogik darf die Seite nie beschädigen */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLeonarisHub);
  } else {
    initLeonarisHub();
  }
})();
